import { useEffect, useState } from "react";
import React from "react";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";

import Footer from "./Footer";
import TopBar from "./Topbar";
import { getNewsPost } from "../utils/newsPosts";

const renderBlock = (block, index) => {
    const blockText = typeof block.text === "string" ? block.text : "";

    if(block.type === "paragraph" && blockText) {
        return (
            <p
                key={index}
                className="text-navy text-base sm:text-lg lg:text-xl leading-relaxed font-poppins"
                dangerouslySetInnerHTML={{ __html: blockText }}
            />
        );
    }

    if(block.type === "html" && blockText) {
        return (
            <div
                key={index}
                className="space-y-6 text-navy text-base sm:text-lg lg:text-xl leading-relaxed font-poppins [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:md:text-3xl [&_h2]:lg:text-4xl [&_h2]:font-semibold [&_h2]:text-navy-2 [&_h2]:mt-8 [&_h2]:sm:mt-10 [&_h2]:lg:mt-12 [&_h3]:text-lg [&_h3]:sm:text-xl [&_h3]:md:text-2xl [&_h3]:lg:text-3xl [&_h3]:font-semibold [&_h3]:text-navy [&_h3]:mt-6 [&_h3]:sm:mt-8 [&_ul]:list-disc [&_ul]:list-inside [&_ul]:space-y-2 [&_ul]:sm:space-y-3 [&_ul]:ml-2 [&_ul]:sm:ml-4 [&_li]:pl-2"
                dangerouslySetInnerHTML={{ __html: blockText }}
            />
        );
    }

    if(block.type === "heading" && blockText) {
        return (
            <h2
                key={index}
                className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-navy-2 mt-8 sm:mt-10 lg:mt-12 font-poppins"
            >
                {blockText}
            </h2>
        );
    }

    if(block.type === "subheading" && blockText) {
        return (
            <h3
                key={index}
                className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-navy mt-6 sm:mt-8 font-poppins"
            >
                {blockText}
            </h3>
        );
    }

    if(block.type === "list" && Array.isArray(block.items)) {
        return (
            <ul
                key={index}
                className="list-disc list-inside text-navy text-base sm:text-lg lg:text-xl leading-relaxed space-y-2 sm:space-y-3 font-poppins ml-2 sm:ml-4"
            >
                {block.items.map((item, idx) => (
                    <li
                        key={idx}
                        className="pl-2"
                        dangerouslySetInnerHTML={{ __html: item }}
                    />
                ))}
            </ul>
        );
    }

    if(block.type === "imageText" && block.image) {
        return (
            <div
                key={index}
                className="flex flex-col md:flex-row gap-6 md:gap-8 items-start"
            >
                <div className="flex-1 space-y-6 sm:space-y-8">
                    {(block.paragraphs || []).map((paragraph, idx) => (
                        <p
                            key={idx}
                            className="text-navy text-base sm:text-lg lg:text-xl leading-relaxed font-poppins"
                            dangerouslySetInnerHTML={{ __html: paragraph }}
                        />
                    ))}
                </div>
                <img
                    src={block.image}
                    alt={block.alt || ""}
                    className="w-full md:w-80 lg:w-96 rounded-lg shadow-md object-cover"
                />
            </div>
        );
    }

    if(block.type === "bold" && blockText) {
        return (
            <p
                key={index}
                className="text-lg sm:text-xl lg:text-2xl text-navy font-bold font-poppins"
            >
                {blockText}
            </p>
        );
    }

    return null;
};

const getVideoEmbedUrl = (value) => {
    if(!value) return "";

    try {
        const parsedUrl = new URL(value);
        const hostname = parsedUrl.hostname.toLowerCase();
        const pathSegments = parsedUrl.pathname.split("/").filter(Boolean);

        if(hostname === "youtu.be") {
            return pathSegments[0]
                ? `https://www.youtube.com/embed/${encodeURIComponent(pathSegments[0])}`
                : "";
        }

        if(
            hostname === "youtube.com" ||
            hostname.endsWith(".youtube.com") ||
            hostname === "youtube-nocookie.com" ||
            hostname.endsWith(".youtube-nocookie.com")
        ) {
            const videoId = parsedUrl.searchParams.get("v") ||
                (["embed", "shorts", "live"].includes(pathSegments[0])
                    ? pathSegments[1]
                    : "");
            return videoId
                ? `https://www.youtube.com/embed/${encodeURIComponent(videoId)}`
                : "";
        }

        if(hostname === "vimeo.com" || hostname.endsWith(".vimeo.com")) {
            const videoId = [...pathSegments]
                .reverse()
                .find((segment) => /^\d+$/.test(segment));
            return videoId ? `https://player.vimeo.com/video/${videoId}` : "";
        }
    } catch{
        return "";
    }

    return "";
};

const BlogHeroSection = () => {
    const { id } = useParams();
    const [blog, setBlog] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        getNewsPost(id)
            .then(setBlog)
            .catch(() => setNotFound(true));
    }, [id]);

    if(notFound) {
        return (
            <div className="flex items-center justify-center min-h-screen text-xl font-semibold text-red-600">
                Blog post not found.
            </div>
        );
    }

    if(!blog) {
        return (
            <div className="flex items-center justify-center min-h-screen text-xl font-semibold text-navy">
                Loading story...
            </div>
        );
    }

    const videoEmbedUrl = getVideoEmbedUrl(blog.videoUrl);

    return (
        <>
            <TopBar />
            <section className="w-full bg-white">
                <div className="relative w-full overflow-hidden">
                    <img
                        src={blog.blogImage || blog.image}
                        alt={blog.heroTitle || blog.title}
                        className="w-full h-[320px] sm:h-[360px] md:h-[420px] lg:h-[480px] xl:h-[520px] 2xl:h-[560px] object-cover object-center"
                    />
                    <div className="absolute inset-0 z-0 bg-black/20"></div>
                    <div className="absolute bottom-0 z-10 w-full px-4 sm:px-6 lg:px-8">
                        <div className="mx-auto mb-4 max-w-4xl rounded bg-navy/70 p-4 sm:mb-6 sm:p-6">
                            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight text-center text-white font-poppins">
                                {blog.heroTitle || blog.title}
                            </h1>
                        </div>
                    </div>
                </div>
            </section>

            <section className="w-full px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24 py-8 sm:py-10 md:py-12 lg:py-16 xl:py-20">
                <div className="max-w-4xl mx-auto space-y-6 sm:space-y-8">
                    {blog.content.map(renderBlock)}
                    {videoEmbedUrl && (
                        <div className="h-[300px] overflow-hidden rounded-lg bg-black shadow-lg sm:h-[420px] lg:h-[520px]">
                            <iframe
                                src={videoEmbedUrl}
                                title={`${blog.title} video`}
                                className="h-full w-full border-0"
                                loading="lazy"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                allowFullScreen
                            />
                        </div>
                    )}
                </div>
            </section>

            <div className="flex justify-center w-full px-4 sm:px-6 pb-8 sm:pb-12">
                <Link
                    to="/news"
                    className="inline-block bg-navy text-white text-sm sm:text-base font-medium font-poppins px-4 sm:px-6 py-2 sm:py-3 rounded-lg shadow hover:bg-navy-2 transition duration-300 ease-in-out transform hover:scale-105"
                >
                    Back to Stories
                </Link>
            </div>
            <section className="bg-white py-6 px-4 sm:px-8 md:px-16 lg:px-32 xl:px-48">
                <div className="w-full mx-auto text-gray-700 text-[10px] sm:text-xs md:text-sm font-poppins italic leading-relaxed">
                    <strong className="text-navy">Disclosure</strong>
                    <p className="text-navy">
                        This article is for informational and educational
                        purposes only. It is not intended as investment advice,
                        a forecast of future performance, or a solicitation to
                        purchase or sell any securities. Data and projections
                        cited are based on third-party sources believed to be
                        reliable but not guaranteed. Investing in private or
                        crowdfunded offerings involves risk, including
                        illiquidity and the possible loss of invested capital.
                    </p>
                </div>
            </section>
            <Footer />
        </>
    );
};

export default BlogHeroSection;
